import React, { Component } from 'react';
import { withRouter } from '../navigation/withNavigation';
import { urlMap } from '../../utils';
import { TextEditor } from '../dashboard/chapterPage/editor/TextEditor';
import WriterModeTimer from '../dashboard/chapterPage/WriterModeTimer';
import DraftIssueSolver from './DraftIssueSolver';
import IssueReadOnlyCard from '../dashboard/components/IssueReadOnlyCard';
import Footer from '../components/Footer';
import ActionButton from '../components/ActionButton';
import Modal from '../components/Modal';
import { mdToHtml, htmlToMd } from '../../utils/markdown';
import { applyThemePref } from '../../utils/theme';
import '../../assets/styles/draft.css';

class DraftWriterMode extends Component {
  editorRef = React.createRef();

  state = {
    loading: true,
    error: null,
    draft: null,
    title: '',
    contentMd: '',
    writerModeSecondsLeft: 25 * 60,
    writerModeTimerRunning: false,
    themePref: 'light',
    coachVisible: true,
    submitting: false,
    submitEmail: '',
    submitEmailError: '',
    submitMessage: '',
    showSubmitForm: false,
    submitError: null,
    showLeaveDialog: false,
  };

  writerModeTimer = null;

  async componentDidMount() {
    // Keep onboarding white by default; still respects explicit user preference.
    let pref = 'light';
    try {
      const cached = localStorage.getItem('themePref');
      pref = cached || 'light';
      if (!cached) localStorage.setItem('themePref', pref);
    } catch {}

    applyThemePref(pref);
    this.setState({ themePref: pref });
    // Make Enter split into <p> blocks (not zero-margin <div>s) so paragraph
    // gaps stay visible and match the \n\n paragraph-break markdown format.
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* unsupported */ }
    await this.loadDraft();
  }

  onThemeChange = (pref) => {
    this.setState({ themePref: pref });
    applyThemePref(pref);
  };

  componentWillUnmount() {
    if (this.writerModeTimer) {
      clearInterval(this.writerModeTimer);
      this.writerModeTimer = null;
    }
  }

  async componentDidUpdate(prevProps) {
    const prevGuid = prevProps.params?.guid;
    const currGuid = this.props.params?.guid;
    if (prevGuid !== currGuid) {
      await this.loadDraft();
    }
  }

  async loadDraft() {
    const guid = this.props.params?.guid;
    if (!guid) {
      this.setState({ loading: false, error: 'Missing onboarding GUID.' });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const res = await fetch(urlMap.bootstrapOnboardingDraft(guid), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Failed to load draft (${res.status})`);

      const draft = await res.json();
      this.setState({
        loading: false,
        draft,
        title: draft?.chapter_title || '',
        contentMd: draft?.chapter_content || '',
        error: null,
      }, () => {
        if (this.editorRef.current) {
          this.editorRef.current.innerHTML = mdToHtml(draft?.chapter_content || '');
        }
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err?.message || 'Failed to load onboarding draft.',
      });
    }
  }

  onTitleChange = (e) => {
    this.setState({ title: e.target.value });
  };

  onEditorInput = () => {
    const html = this.editorRef.current?.innerHTML || '';
    this.setState({ contentMd: htmlToMd(html) });
  };

  onEditorKeyDown = () => {};

  onEditorPaste = () => {};

  onClaimAccount = () => {
    const guid = this.props.params?.guid;
    const qs = guid ? `?draftGuid=${encodeURIComponent(guid)}` : '';
    this.props.navigate(`/register${qs}`);
  };

  onBackArrowClick = () => {
    this.setState({ showLeaveDialog: true });
  };

  onCloseLeaveDialog = () => {
    this.setState({ showLeaveDialog: false });
  };

  onLeaveAnyway = () => {
    this.setState({ showLeaveDialog: false });
    this.props.navigate('/');
  };

  onToggleSubmitForm = () => {
    this.setState((prev) => ({
      showSubmitForm: !prev.showSubmitForm,
      submitError: null,
    }));
  };

  onSubmitEmailChange = (e) => {
    const email = e.target.value.trim();
    const isValid = this.isValidEmail(email);
    this.setState({
      submitEmail: email,
      submitEmailError: email && !isValid ? 'Please enter a valid email address.' : '',
    });
  };

  onSubmitMessageChange = (e) => {
    this.setState({ submitMessage: e.target.value });
  };

  isValidEmail = (email) => {
    if (!email || email.length === 0 || email.length > 254) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);
  };

  onSubmitForReview = async () => {
    const guid = this.props.params?.guid;
    if (!guid || this.state.submitting) return;
    
    // Validate email before submit
    if (!this.isValidEmail(this.state.submitEmail)) {
      this.setState({ submitEmailError: 'Please enter a valid email address.' });
      return;
    }

    this.setState({ submitting: true, submitError: null });
    try {
      const res = await fetch(urlMap.submitOnboardingDraft(guid), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: this.state.submitEmail,
          message: this.state.submitMessage || undefined,
          chapterTitle: this.state.title,
          chapterContent: this.state.contentMd,
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }
      const updated = await res.json();
      this.setState({
        draft: updated,
        submitting: false,
        showSubmitForm: false,
        submitError: null,
        submitEmail: '',
        submitEmailError: '',
        submitMessage: '',
      });
    } catch (err) {
      this.setState({
        submitting: false,
        submitError: err?.message || 'Failed to submit draft for review.',
      });
    }
  };

  startWriterModeTimer = () => {
    if (this.state.writerModeTimerRunning) return;
    this.setState({ writerModeTimerRunning: true }, () => {
      if (this.writerModeTimer) clearInterval(this.writerModeTimer);
      this.writerModeTimer = setInterval(() => {
        this.setState((prev) => {
          if (prev.writerModeSecondsLeft <= 1) {
            clearInterval(this.writerModeTimer);
            this.writerModeTimer = null;
            return { writerModeSecondsLeft: 0, writerModeTimerRunning: false };
          }
          return { writerModeSecondsLeft: prev.writerModeSecondsLeft - 1 };
        });
      }, 1000);
    });
  };

  render() {
    const {
      loading,
      error,
      title,
      draft,
      writerModeSecondsLeft,
      writerModeTimerRunning,
      themePref,
      coachVisible,
      submitting,
      submitEmail,
      submitEmailError,
      submitMessage,
      showSubmitForm,
      submitError,
      showLeaveDialog,
    } = this.state;

    if (loading) {
      return (
        <div className="home onboarding-writer-page onboarding-writer-page--loading">
          <h2>Loading writer mode...</h2>
        </div>
      );
    }

    const submissionStatus = draft?.submission_status || 'draft';
    const canSubmit = !!draft?.source_chapter_id && submissionStatus === 'draft';
    const isSubmitted = submissionStatus !== 'draft';

    return (
      <div className="home onboarding-writer-page">
        {error && (
          <div className="onboarding-alert onboarding-alert--spaced">
            {error}
          </div>
        )}

        <div className="editor-root focus-lock writer-mode show-coach">
          <div className="editor-shell">
            <div className="onboarding-editor-column">
            <div className="onboarding-draft-title-row">
              <ActionButton
                className="btn subtle pf-back draft-editor-back-arrow"
                onClick={this.onBackArrowClick}
                ariaLabel="Back to home"
                title="Back to home"
              >
                ←
              </ActionButton>
              <input
                value={title}
                onChange={this.onTitleChange}
                placeholder="Chapter title"
                className="onboarding-title-input"
              />              
              {canSubmit && (
                <button
                  type="button"
                  className="btn primary draft-title-row-submit-btn"
                  onClick={this.onToggleSubmitForm}
                  disabled={submitting}
                >
                  Send solution to owner
                </button>
              )}
              {isSubmitted && (
                <div
                  className={`draft-title-row-submit-status draft-submit-status draft-submit-status--${submissionStatus}`}
                >
                  {submissionStatus === 'submitted' && 'Draft submitted'}
                  {submissionStatus === 'accepted' && 'Accepted by owner'}
                  {submissionStatus === 'rejected' && 'Owner requested changes — submission was not accepted'}
                </div>
              )}
              <button
                className="btn subtle draft-title-row-claim-btn"
                onClick={this.onClaimAccount}
              >
                Claim your account
              </button>
            </div>

            {draft?.metadata?.seeded_issue && (
              <div className="draft-mobile-issue">
                <IssueReadOnlyCard
                  issue={draft.metadata.seeded_issue}
                  defaultExpanded={true}
                  onStartFlow={null}
                />
              </div>
            )}

            <TextEditor
              editorRef={this.editorRef}
              isWriterMode={true}
              onInput={this.onEditorInput}
              onKeyDown={this.onEditorKeyDown}
              onPaste={this.onEditorPaste}
              topContent={null}
            />
            <Footer />
            </div>

            <div className="onboarding-side-column">
              {coachVisible ? (
                <DraftIssueSolver
                  guid={draft?.guid}
                  issue={draft?.metadata?.seeded_issue || null}
                  onClaimAccount={this.onClaimAccount}
                  onClose={() => this.setState({ coachVisible: false })}
                />
              ) : (
                <button
                  type="button"
                  className="btn subtle draft-show-coach-btn"
                  onClick={() => this.setState({ coachVisible: true })}
                >
                  Coach
                </button>
              )}

              <div className="onboarding-theme-switch-wrap" aria-label="Theme preference">
                <span className="onboarding-theme-switch-label">light</span>
                <label className="onboarding-theme-switch" aria-label="Toggle dark mode">
                  <input
                    type="checkbox"
                    checked={themePref === 'dark'}
                    onChange={(e) => this.onThemeChange(e.target.checked ? 'dark' : 'light')}
                  />
                  <span className="onboarding-theme-switch-slider" />
                </label>
                <span className="onboarding-theme-switch-label">dark</span>
              </div>
            </div>
          </div>
        </div>

        {(canSubmit && showSubmitForm) && (
          <div className="draft-submission-overlay" onClick={submitting ? () => {} : this.onToggleSubmitForm}>
            <div className="draft-submission-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="draft-submission-modal-close"
                onClick={submitting ? () => {} : this.onToggleSubmitForm}
                disabled={submitting}
                type="button"
              >
                ×
              </button>
              <div className="draft-leave-dialog">
                <h3 className="draft-leave-dialog__title">Send solution to owner</h3>
                <p className="draft-leave-dialog__body">
                  Email address <span style={{ color: '#d32f2f' }}>*</span>
                </p>
                <input
                  type="email"
                  id="draft-submit-email"
                  className="draft-submit-form__email"
                  value={submitEmail}
                  onChange={this.onSubmitEmailChange}
                  placeholder="your.email@example.com"
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    marginBottom: '8px',
                    border: submitEmailError ? '2px solid #d32f2f' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
                {submitEmailError && (
                  <div className="draft-submit-form__error" style={{ marginBottom: '12px' }}>
                    {submitEmailError}
                  </div>
                )}
                <p className="draft-leave-dialog__body">
                  Message (optional)
                </p>
                <textarea
                  id="draft-submit-message"
                  className="draft-submit-form__textarea"
                  value={submitMessage}
                  onChange={this.onSubmitMessageChange}
                  rows={3}
                  maxLength={2000}
                  placeholder="Anything the owner should know about your changes?"
                  disabled={submitting}
                />
                {submitError && (
                  <div className="draft-submit-form__error">{submitError}</div>
                )}
                <div className="draft-leave-dialog__actions">
                  <button
                    type="button"
                    className="btn subtle"
                    onClick={this.onToggleSubmitForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn primary draft-leave-dialog__claim"
                    onClick={this.onSubmitForReview}
                    disabled={submitting || !this.isValidEmail(submitEmail)}
                  >
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Modal isOpen={showLeaveDialog} onClose={this.onCloseLeaveDialog}>
          <div className="draft-leave-dialog">
            <h3 className="draft-leave-dialog__title">Make an account to keep your work</h3>
            <p className="draft-leave-dialog__body">
              Your draft lives only in this browser session. Claim your account so
              you don’t lose it.
            </p>
            <div className="draft-leave-dialog__actions">
              <button
                type="button"
                className="btn subtle"
                onClick={this.onLeaveAnyway}
              >
                Leave anyway
              </button>
              <button
                type="button"
                className="btn primary draft-leave-dialog__claim"
                onClick={this.onClaimAccount}
              >
                Claim your account
              </button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

export default withRouter(DraftWriterMode);
